import { useState, type FormEvent } from "react";
import { ApiError, postContact } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useBotProtection } from "../../hooks/useBotProtection";
import { HoneypotField } from "./HoneypotField";
import { TurnstileWidget } from "./TurnstileWidget";

export function ContactForm() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setError(null);
    setBusy(true);
    try {
      await postContact({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim() || undefined,
        body: body.trim(),
        ...botFields,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not deliver your message");
      resetBotProtection();
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="form-success form-success--card">
        <div className="sec-eyebrow"><em>message sent</em></div>
        <h3 className="sec-title sec-title-sm">Thank you.</h3>
        <p className="form-hint">
          We received your note and will reply by email within a day or two.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="form-stack" aria-busy={busy}>
      <HoneypotField value={website} onChange={setWebsite} />
      <div className="form-row">
        <label>
          <span>Your name</span>
          <input
            type="text"
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            autoComplete="name"
          />
        </label>
        <label>
          <span>Email</span>
          <input
            type="email"
            required
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            autoComplete="email"
          />
        </label>
      </div>
      <label>
        <span>Subject <em className="form-label-opt">optional</em></span>
        <input
          type="text"
          maxLength={160}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={busy}
        />
      </label>
      <label>
        <span>Message</span>
        <textarea
          required
          minLength={5}
          maxLength={2000}
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={busy}
          placeholder="Tell us what's on your heart…"
        />
      </label>
      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !ready}>
          {busy ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
}
